/**
 * One conversation about one thing on the graph.
 *
 * Extracted from the old `AskPanel` so a conversation can render WHERE THE
 * THING IS rather than in a tab of its own. Since the feed became the thread's
 * conversation surface, the things this component renders under are the ones
 * that live OUTSIDE the feed: a candidate's card in Breakthroughs, and the
 * fallback for a conversation whose candidate has since been dismissed. It
 * stays pinned to whatever target it is mounted for — the feed's per-turn
 * subjects are the other way of keeping the same promise.
 *
 * What did not move is everything the channel promises, and it is restated
 * here because this is a surface that carries it:
 *
 *  - It is PINNED to one target and never follows the selection. A
 *    conversation that drifted would silently become a conversation about
 *    something else halfway through.
 *  - It says, once, that the answers have not been checked against anything.
 *    This channel has no search and therefore no citation gate, and it is the
 *    one surface in the app where the model speaks without one.
 *  - ASKING takes no lock and no step of history, so it works while a run is
 *    going and changes nothing on the canvas. The presses under a finished
 *    answer are the exception and are meant to be: they walk it back to the
 *    graph through the doors that were already there, at those doors' own
 *    prices. `core/promote.ts` has the rule they keep — the answer is a priced
 *    pass's INPUT, never the output that lands.
 *
 * The transcript survives, stored beside the graph and never in it
 * (`core/conversations.ts`), and the live copy sits in module scope
 * (`ui/askLive.ts`) so an unmount mid-answer costs nothing — the reasons are
 * with the state, where the feed shares them.
 */

import { useEffect, useRef } from 'react'
import { useCanAsk, useStore, type ChatTarget } from '../core/store'
import { briefFor, stopAsking } from '../engine/chat'
import {
  OPENERS,
  clearConversation,
  liveKey,
  handsBack,
  sendOn,
  streamingElsewhere,
  threadKeyOf,
  useLiveConversation,
  setLiveDraft,
} from './askLive'
import {
  PROMOTE_PAID,
  PROMOTE_PRICE_VERB,
  promotableTurn,
  promotesFor,
  type PromoteKind,
} from '../core/promote'
import { Icon } from './components/Icon'
import { promoteRefusal, takePromote } from './promotePress'
import { SpentMeta, VerbPrice } from './VerbPrice'
import { Prose } from './prose'
import { STATUS_WORD } from '../core/vocabulary'

export function AskThread({
  target,
  /** True where the conversation floats free of the thing it is about — a
   *  candidate question whose candidate is off the list. It then names its own
   *  pin, because nothing beside it does. */
  detached = false,
}: {
  target: ChatTarget
  detached?: boolean
}) {
  const askAbout = useStore((s) => s.askAbout)
  const select = useStore((s) => s.select)
  const revealNode = useStore((s) => s.revealNode)
  const session = useStore((s) => s.session)
  // The chat role's key — a routed one where routed — through the same selector
  // the feed asks: a key for the wrong provider is indistinguishable from none.
  const ready = useCanAsk()
  const runLive = useStore((s) => s.run !== null && s.run.phase !== 'ended')
  // Everything a promote press's refusal reads, taken one field at a time —
  // a selector returning an object would build a new one every render and never
  // compare equal. `storeBusy` and not `busy`: the local one below means THIS
  // conversation is streaming, which is a different question and not the one a
  // pass asks. `VerbPrice` subscribes to its own; the two readings are of the
  // same store in the same render.
  const storeBusy = useStore((s) => s.busy)
  const modelId = useStore((s) => s.modelId)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const roleModels = useStore((s) => s.roleModels)

  const key = threadKeyOf(target)
  const sessionId = session.id
  const k = liveKey(sessionId, key)

  const state = useLiveConversation(sessionId, key)
  const turns = state.turns
  const draft = state.draft
  const setDraft = (v: string) => setLiveDraft(sessionId, key, v)
  const busy = turns.some((t) => t.live)
  // Another conversation is still arriving. Asking now would abort it inside
  // the engine and leave its partial answer looking finished — see `streaming`
  // in askLive.ts. Read from module scope rather than from the snapshot, so it
  // is `announceAll` replacing this conversation's entry that re-renders the
  // line; a bare notify would not, and for a while did not.
  const elsewhere = streamingElsewhere(k)
  const tail = useRef<HTMLDivElement | null>(null)

  // NOTE: no reset here, and no `stopAsking` on a pin change. Each pin owns its
  // own entry in askLive, so switching between them shows the right conversation
  // without touching the wrong one — and an answer still arriving keeps
  // arriving into the conversation that paid for it, wherever the reader has
  // moved to meanwhile.

  useEffect(() => {
    // Only once something has arrived: scrolling on mount would drag the
    // reading column down to a conversation the reader has not looked at yet.
    if (turns.length > 0) tail.current?.scrollIntoView({ block: 'nearest' })
  }, [turns])

  const send = (q: string) => {
    const question = q.trim()
    if (!question || busy || elsewhere) return
    // A question that failed comes back to this box — beside the key field a
    // key refusal opened (`askForKey`), or under the turn that says a rate limit
    // turned it away — rather than being lost to it (`handsBack`).
    void sendOn(target, sessionId, key, question).then((kind) => {
      if (handsBack(kind)) setLiveDraft(sessionId, key, question)
    })
  }

  // A question handed over by the composer. The composer pins node and graph
  // targets, whose conversations the FEED consumes — this effect takes a
  // proposal only when its own pin is the one the proposal was made against,
  // so the two consumers can never split one handoff.
  //
  // Taken on the TRANSITION into a question, never on the value — the
  // `sigRewrite` precedent, and here it is load-bearing rather than tidy.
  // StrictMode invokes a mount effect twice against the SAME closure, so
  // clearing the store inside the effect does not stop the second run from
  // seeing the question it already sent: one press, two calls, two charges.
  // The latch resets when the store goes back to null, which it does on the
  // very next render, so proposing the same words twice still asks twice.
  const proposedQuestion = useStore((s) => s.proposedQuestion)
  const proposeQuestion = useStore((s) => s.proposeQuestion)
  const chatPin = useStore((s) => s.chat)
  const taken = useRef<string | null>(null)
  useEffect(() => {
    if (proposedQuestion === null) {
      taken.current = null
      return
    }
    if (chatPin === null || threadKeyOf(chatPin) !== key) return
    if (taken.current === proposedQuestion) return
    taken.current = proposedQuestion
    proposeQuestion(null)
    if (ready && !elsewhere && !busy) send(proposedQuestion)
    // Without a key, while another conversation is still arriving, or while
    // THIS one is — `send` refuses all three — the words would simply vanish:
    // the handoff is already spent and the composer has cleared its own box.
    // So they land in this box instead, beside the placeholder that says what
    // it is waiting on.
    else setDraft(proposedQuestion)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedQuestion, key])

  // The reader's Clear: the conversation goes, the pin is released, and a toast
  // offers it back (D339). A put-back re-pins this thread too, because the Clear
  // also closed it and a transcript restored behind a closed thread would look
  // like a button that did nothing — but only while nothing else has been pinned
  // and the exploration is still the one it was cleared in: the reader's own
  // presses since are not the toast's to undo. "The one it was cleared in" is the
  // id the turns went back under, not the one this render saw — a paid pass on
  // the worked example renames it under the offer.
  const clear = () => {
    clearConversation(k, (restoredIn) => {
      const st = useStore.getState()
      if (st.chat === null && st.session.id === restoredIn) st.askAbout(target)
    })
    askAbout(null)
  }

  const found = briefFor(session, target)
  if (!found) {
    return (
      <div className="askthread">
        <p className="gnote gnote--warn">
          {target.kind === 'node'
            ? 'That node is no longer on the graph, so there is nothing left to ask about.'
            : 'That candidate is no longer in the list.'}{' '}
          <button
            className="linkish"
            onClick={() => {
              // The same discard as the ordinary Clear. This button used to
              // release the pin and nothing else, so a pruned node's transcript
              // stayed on disk under a control that said Clear.
              clear()
            }}
          >
            Clear
          </button>
        </p>
      </div>
    )
  }

  const goToPin = () => {
    if (target.kind !== 'node') return
    revealNode(target.id)
    select(target.id)
  }

  // The doors out of this conversation and back into the record. Empty for a
  // graph-wide question and for a bridge, which name no single node to act on,
  // and shorter on the seed, which has no revision editor and no outcome form —
  // `promotesFor` decides all of that, not this file.
  const nodeId = target.kind === 'node' ? target.id : null
  const promotes =
    target.kind === 'node'
      ? promotesFor({ kind: 'node', isRoot: session.nodes[target.id]?.parentId === null })
      : promotesFor(target)
  // Which answer the row acts on, decided in `core/promote.ts` so the rule can be
  // tested — this component cannot be, the suite being Node-only. One row for the
  // conversation rather than a row per turn: four doors repeated down a
  // transcript is four chances to press the one meant for a different answer.
  const promotable = promotes.length > 0 ? promotableTurn(turns) : undefined
  // The exact reading `takePromote` guards on, so a row that greys out and a
  // press that refuses cannot come to disagree — and the sentence printed under
  // the rows is the sentence the press would have raised, not a second copy
  // written here. Only the refused kinds land in the map, so `has` answers
  // "is this row pressable" without a second predicate.
  const routing = { session, modelId, roleModels, keys, customBase, customModel, busy: storeBusy }
  const refusals = new Map<PromoteKind, string>()
  if (nodeId !== null) {
    for (const p of promotes) {
      const why = promoteRefusal(routing, p.kind, nodeId)
      if (why !== null) refusals.set(p.kind, why)
    }
  }

  return (
    <div className="askthread">
      <div className="askthread-head">
        <span className="eyebrow">
          {detached
            ? target.kind === 'graph'
              ? 'Asking about this exploration'
              : 'Asking about another claim'
            : 'Asking about this'}
        </span>
        <div className="askthread-acts">
          {detached && target.kind === 'node' && (
            <button className="linkish" onClick={goToPin}>
              Go to it
            </button>
          )}
          <button className="linkish" onClick={clear}>
            Clear
          </button>
        </div>
      </div>
      {/* The pin, spelled out only where nothing beside it does the naming. In
          place under the card it is about, the card IS the pin. */}
      {detached && <p className="ask-pin">{found.title}</p>}

      <p className="gnote gnote--quiet">
        Answers here are the model reading what is already on the graph. It has no search on this
        channel, so nothing it says has been checked against a source — press{' '}
        <span className="kbd">V</span> on a node for that. Asking changes nothing, and the
        transcript is kept beside this exploration rather than inside it, so no export or share link
        carries it.
        {promotes.length > 0 && (
          <>
            {' '}
            An answer can still send you back to the graph: the presses under one run an ordinary
            pass that has been told what you read, and what lands is that pass's own reasoning at
            its own price.
          </>
        )}
      </p>
      {runLive && (
        <p className="gnote gnote--quiet">
          A run is going. Asking does not interrupt it — the two do not share a turn.
        </p>
      )}

      {turns.length === 0 && (
        <div className="askthread-openers">
          {OPENERS.map((o) => (
            <button
              key={o}
              className="ask-opener"
              disabled={!ready || elsewhere}
              onClick={() => send(o)}
            >
              {o}
            </button>
          ))}
        </div>
      )}

      <div className="ask-thread">
        {turns.map((t) => (
          <div key={t.id} className="ask-turn">
            <p className="ask-q">{t.question}</p>
            <Prose text={t.answer} live={t.live} />
            {t.live && t.waiting && <p className="gnote">{t.waiting}</p>}
            {t.error && <p className="gnote gnote--warn">{t.error}</p>}
          </div>
        ))}
        <div ref={tail} />
      </div>

      {promotable && nodeId !== null && (
        <div className="group" style={{ gap: 'var(--s2)' }}>
          <div className="tierline">
            <span className="eyebrow">Take it back to the graph</span>
            <SpentMeta />
          </div>
          <div className="actions ask-promotes">
            {promotes.map((p) => {
              const verb = PROMOTE_PRICE_VERB[p.kind]
              return (
                <button
                  key={p.kind}
                  // Wide, per the rule the narrow verb cells are the exception
                  // to: three of these four are paid passes, and a paid pass
                  // says what it buys before it is pressed. Here the reader has
                  // no six-verb vocabulary to fall back on either — this row is
                  // the first place any of it is named.
                  className="action action--wide"
                  // Refused without leaving the tab order: this row stays after
                  // a press, and a `disabled` one greyed under the pass its own
                  // press had started, dropping focus on the page body. The
                  // press says why (`takePromote` raises the sentence below).
                  aria-disabled={refusals.has(p.kind) || undefined}
                  onClick={() =>
                    takePromote(p.kind, nodeId, {
                      question: promotable.question,
                      answer: promotable.answer,
                    })
                  }
                >
                  <b>
                    {p.label}
                    {verb && <VerbPrice verb={verb} />}
                    {/* The free one says so AT REST, because the figure beside
                        the other three only appears on hover and focus — so
                        without this the row's honest answer to "which of these
                        spends" was available only to a reader already reaching
                        for one. `PROMOTE_PAID` and not `verb === null`: a null
                        price verb covers two opposite facts, and printing "no
                        model call" for the other one — paid but unquotable —
                        would be the lie that table exists to prevent. The
                        marker is the node card's own (`vp-spent`, "No model
                        call"), because this is the same statement it makes. */}
                    {!PROMOTE_PAID[p.kind] && <span className="vp-spent">{STATUS_WORD.free}</span>}
                  </b>
                  <span>{p.does}</span>
                </button>
              )
            })}
          </div>
          {/* The refusals, OUTSIDE the rows they stop — the coach's card does
              the same, and for a reason that is visible the moment you try the
              other way: `.action:disabled` is 40% opacity, so a sentence
              explaining why a row cannot be pressed is least readable at
              exactly the moment it matters most. Deduplicated because one cause
              usually stops several rows at once, and printed in the words the
              press itself would have used, so the note and the toast cannot
              come to say different things. */}
          {[...new Set(refusals.values())].map((why) => (
            <p key={why} className="gnote gnote--warn">
              {why}
            </p>
          ))}
        </div>
      )}

      <div className="group" style={{ gap: 'var(--s2)' }}>
        <textarea
          className="textarea"
          rows={2}
          value={draft}
          placeholder={
            !ready
              ? 'Connect a key to ask.'
              : elsewhere
                ? 'One thought at a time — another answer is still arriving.'
                : target.kind === 'graph'
                  ? 'Ask about this graph…'
                  : 'Ask about this…'
          }
          disabled={!ready || elsewhere}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, so a question is one keystroke. Shift-Enter is the
            // newline, which is the convention every chat surface has taught.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              send(draft)
            }
          }}
          aria-label="Ask about this"
        />
        <div style={{ display: 'flex', gap: 'var(--s2)' }}>
          <button
            className="btn btn--primary"
            disabled={!ready || busy || elsewhere || draft.trim().length < 2}
            title={
              elsewhere
                ? 'Another conversation is still arriving. Asking now would cut it off partway.'
                : undefined
            }
            onClick={() => send(draft)}
          >
            <Icon name="spark" size={13} />
            Ask
          </button>
          {busy && (
            <button className="btn btn--outline" onClick={stopAsking}>
              <Icon name="stop" size={13} />
              Stop
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
